// ============================
// File: src/features/aoa/AoAWorkbench.jsx
// ============================
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Scale, Layers, Trophy, DollarSign, TrendingUp, SlidersHorizontal, Table2,
  BarChart3, AlertTriangle, ListChecks, FileText, Star, ArrowRight, RotateCcw,
  CalendarClock, Target, FlaskConical,
} from "lucide-react";
import { card, th, td, btn, Kpi, Pill } from "../../pages/cyberShared";
import {
  CRITERIA, DEFAULT_WEIGHTS, buildMockAoa, rankAlternatives, fmtM, fmtSignedM,
} from "./mockAoa";

/**
 * Analysis of Alternatives — sits between Discovery and Enhanced Discovery (POC).
 *
 * Discovery establishes the need; AoA asks "what is the most effective,
 * affordable, and lowest-risk way to meet it?" by comparing candidate solutions
 * on lifecycle cost, benefit, risk, schedule and fit. The POC then tests the
 * preferred alternative, and TAB reads the recommendation summary below.
 *
 * MOCK: all figures come from mockAoa.js. Persistence is a follow-up — the
 * preferred alternative lives in localStorage only, and "Complete AoA" just
 * navigates to the POC stage without writing current_stage on the backend.
 */

const ACCENT = "#9d80ff";
const TEXT = "#e8eef5";
const MUTED = "#8aa0b6";
const BORDER = "#1c3d5c";

const RISK_TONE = {
  Low: { bg: "#0f323c", fg: "#85e3af" },
  Moderate: { bg: "#313633", fg: "#facb7e" },
  High: { bg: "#322c35", fg: "#ffa685" },
};

const prefKey = (demandId) => `aiea.aoa.preferred.${demandId}`;

function readPreferred(demandId) {
  try {
    return window.localStorage.getItem(prefKey(demandId));
  } catch {
    return null;
  }
}

function writePreferred(demandId, altId) {
  try {
    if (altId) window.localStorage.setItem(prefKey(demandId), altId);
    else window.localStorage.removeItem(prefKey(demandId));
  } catch {
    /* storage unavailable (private mode, blocked) — selection stays in memory */
  }
}

export function MockDataBadge({ children = "Mock data" }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, background: "#3a2e12",
      color: "#facb7e", border: "1px solid #7a5a1a", fontSize: 10.5, fontWeight: 700,
      padding: "3px 9px", borderRadius: 999, textTransform: "uppercase", letterSpacing: .5,
      whiteSpace: "nowrap",
    }}>
      <AlertTriangle size={11} /> {children}
    </span>
  );
}

function SectionTitle({ icon: Icon, children, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
      {Icon && <Icon size={15} style={{ color: ACCENT }} />}
      <h3 style={{ fontSize: 13.5, fontWeight: 700, color: TEXT, margin: 0, flex: 1 }}>
        {children}
      </h3>
      {right}
    </div>
  );
}

function ScoreCell({ value }) {
  const tone = value >= 4 ? "#85e3af" : value >= 3 ? "#facb7e" : "#ffa685";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontWeight: 700, color: tone, minWidth: 10 }}>{value}</span>
      <span style={{ display: "inline-flex", gap: 2 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <span key={i} style={{ width: 5, height: 10, borderRadius: 1,
                                 background: i <= value ? tone : "#102942" }} />
        ))}
      </span>
    </span>
  );
}

/* ------------------------------------------------------------ weights */
function WeightsPanel({ weights, onChange, onReset }) {
  const total = CRITERIA.reduce((s, c) => s + (Number(weights[c.id]) || 0), 0);
  return (
    <div style={{ ...card, padding: 16 }}>
      <SectionTitle
        icon={SlidersHorizontal}
        right={<button type="button" style={btn} onClick={onReset}><RotateCcw size={12} /> Reset</button>}
      >
        Criteria weights
      </SectionTitle>
      <p style={{ fontSize: 11.5, color: "#6b8aa8", margin: "0 0 12px" }}>
        Weights are normalised to 100%. Scores and ranks update as you drag.
      </p>
      {CRITERIA.map((c) => {
        const raw = Number(weights[c.id]) || 0;
        const pct = total ? Math.round((raw / total) * 100) : 0;
        return (
          <div key={c.id} style={{ marginBottom: 11 }} title={c.help}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12,
                          color: "#c3d2e0", marginBottom: 3 }}>
              <span>{c.label}</span>
              <span style={{ fontWeight: 700, color: TEXT, fontVariantNumeric: "tabular-nums" }}>{pct}%</span>
            </div>
            <input
              type="range" min={0} max={50} step={1} value={raw}
              onChange={(e) => onChange(c.id, Number(e.target.value))}
              aria-label={`${c.label} weight`}
              style={{ width: "100%", accentColor: ACCENT }}
            />
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------ cost chart */
function CostChart({ ranked, recommendedId }) {
  const max = Math.max(...ranked.map((a) => a.fiveYearCost), 1);
  const ordered = [...ranked].sort((a, b) => a.id.localeCompare(b.id));
  return (
    <div style={{ ...card, padding: 16 }}>
      <SectionTitle icon={BarChart3}>5-year lifecycle cost</SectionTitle>
      <div style={{ display: "flex", gap: 14, fontSize: 11, color: MUTED, marginBottom: 12 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: ACCENT }} /> One-time
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: "#3d6b94" }} /> O&amp;M (5 yr)
        </span>
      </div>
      {ordered.map((a) => {
        const om = a.annualOM * 5;
        const isRec = a.id === recommendedId;
        return (
          <div key={a.id} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12,
                          color: isRec ? TEXT : "#c3d2e0", marginBottom: 3 }}>
              <span style={{ fontWeight: isRec ? 700 : 500 }}>
                {a.name} {isRec && <Star size={11} style={{ color: ACCENT, verticalAlign: -1 }} />}
              </span>
              <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtM(a.fiveYearCost)}</span>
            </div>
            <div style={{ display: "flex", height: 12, background: "#102942", borderRadius: 3,
                          overflow: "hidden", outline: isRec ? `1px solid ${ACCENT}` : "none" }}>
              <div title={`One-time ${fmtM(a.oneTime)}`}
                   style={{ width: `${(a.oneTime / max) * 100}%`, background: ACCENT }} />
              <div title={`O&M ${fmtM(om)}`}
                   style={{ width: `${(om / max) * 100}%`, background: "#3d6b94" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------ matrix */
function ComparisonMatrix({ ranked, recommendedId, preferredId }) {
  return (
    <div style={{ ...card, padding: 16 }}>
      <SectionTitle icon={Table2}>Comparison matrix</SectionTitle>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Rank</th>
              <th style={th}>Alternative</th>
              {CRITERIA.map((c) => <th key={c.id} style={th} title={c.help}>{c.short}</th>)}
              <th style={{ ...th, textAlign: "right" }}>5-yr cost</th>
              <th style={{ ...th, textAlign: "right" }}>Weighted</th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((a) => {
              const isRec = a.id === recommendedId;
              return (
                <tr key={a.id} style={{ background: isRec ? "rgba(157,128,255,.10)" : "transparent" }}>
                  <td style={{ ...td, fontSize: 16, fontWeight: 700,
                               color: isRec ? ACCENT : "#6b8aa8" }}>{a.rank}</td>
                  <td style={{ ...td, minWidth: 170 }}>
                    <div style={{ fontWeight: 600, color: TEXT }}>{a.name}</div>
                    <div style={{ display: "flex", gap: 5, marginTop: 3, flexWrap: "wrap" }}>
                      {isRec && <Pill tone={{ bg: "#1a2a49", fg: "#c4b3ff" }}>Highest score</Pill>}
                      {a.id === preferredId && <Pill tone={{ bg: "#0f323c", fg: "#85e3af" }}>Preferred</Pill>}
                    </div>
                  </td>
                  {CRITERIA.map((c) => (
                    <td key={c.id} style={td}><ScoreCell value={a.scores[c.id]} /></td>
                  ))}
                  <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {fmtM(a.fiveYearCost)}
                  </td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, fontSize: 14,
                               color: isRec ? ACCENT : TEXT, fontVariantNumeric: "tabular-nums" }}>
                    {a.weighted.toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 11, color: "#6b8aa8", margin: "10px 0 0" }}>
        Scores 1–5, where 5 is always better (cheapest, lowest risk, fastest). Weighted total is out of 5.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------ detail cards */
function AlternativeCard({ alt, isRec, isPreferred, onPrefer }) {
  const tone = RISK_TONE[alt.riskLevel] || RISK_TONE.Moderate;
  return (
    <div style={{ ...card, padding: 16, display: "flex", flexDirection: "column",
                  border: `1px solid ${isPreferred ? "#34d17a" : isRec ? ACCENT : BORDER}` }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#6b8aa8", paddingTop: 2 }}>#{alt.rank}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>{alt.name}</div>
          <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap" }}>
            <Pill tone={tone}>{alt.riskLevel} risk</Pill>
            {isRec && <Pill tone={{ bg: "#1a2a49", fg: "#c4b3ff" }}>Highest score</Pill>}
            {isPreferred && <Pill tone={{ bg: "#0f323c", fg: "#85e3af" }}>Preferred</Pill>}
          </div>
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: isRec ? ACCENT : TEXT }}>
          {alt.weighted.toFixed(2)}
        </div>
      </div>
      <p style={{ fontSize: 12, color: "#c3d2e0", lineHeight: 1.5, margin: "4px 0 10px" }}>{alt.description}</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 8,
                    fontSize: 11.5, marginBottom: 10 }}>
        {[
          ["One-time", fmtM(alt.oneTime)],
          ["Annual O&M", fmtM(alt.annualOM)],
          ["5-yr cost", fmtM(alt.fiveYearCost)],
          ["NPV vs status quo", alt.id === "opt1" ? "—" : fmtSignedM(alt.npvVsStatusQuo)],
          ["Est. benefit / yr", fmtM(alt.annualBenefit)],
          ["IOC / FOC", alt.ioc ? `${alt.ioc} / ${alt.foc} mo` : "In place"],
        ].map(([k, v]) => (
          <div key={k} style={{ background: "#0b1a2d", borderRadius: 8, padding: "6px 8px" }}>
            <div style={{ color: "#6b8aa8", fontSize: 10, textTransform: "uppercase", letterSpacing: .3 }}>{k}</div>
            <div style={{ color: TEXT, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 11.5, color: MUTED, marginBottom: 8 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 700,
                       color: "#a8bccf", marginBottom: 3 }}><Target size={12} /> Mission fit</span>
        <div style={{ color: "#c3d2e0", lineHeight: 1.45 }}>{alt.missionFit}</div>
      </div>

      <div style={{ fontSize: 11.5, marginBottom: 8 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 700,
                       color: "#a8bccf" }}><AlertTriangle size={12} /> Top risks</span>
        <ul style={{ margin: "4px 0 0", paddingLeft: 18, color: "#c3d2e0", lineHeight: 1.45 }}>
          {alt.risks.map((r) => <li key={r}>{r}</li>)}
        </ul>
      </div>

      <div style={{ fontSize: 11.5, marginBottom: 12 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 700,
                       color: "#a8bccf" }}><ListChecks size={12} /> Assumptions</span>
        <ul style={{ margin: "4px 0 0", paddingLeft: 18, color: "#c3d2e0", lineHeight: 1.45 }}>
          {alt.assumptions.map((r) => <li key={r}>{r}</li>)}
        </ul>
      </div>

      <div style={{ marginTop: "auto" }}>
        <button
          type="button"
          onClick={() => onPrefer(isPreferred ? null : alt.id)}
          style={{ ...btn, width: "100%", justifyContent: "center",
                   ...(isPreferred
                     ? { background: "#0f323c", borderColor: "#34d17a", color: "#85e3af" }
                     : {}) }}
        >
          <Star size={12} /> {isPreferred ? "Preferred alternative (click to clear)" : "Mark as preferred alternative"}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ recommendation */
function Recommendation({ ranked, recommended, preferred, demandTitle, weights }) {
  const chosen = preferred || recommended;
  const statusQuo = ranked.find((a) => a.id === "opt1");
  const runnerUp = ranked.find((a) => a.id !== chosen.id);
  const total = CRITERIA.reduce((s, c) => s + (Number(weights[c.id]) || 0), 0) || 1;
  const topCriteria = [...CRITERIA]
    .sort((a, b) => (weights[b.id] || 0) - (weights[a.id] || 0))
    .slice(0, 2)
    .map((c) => `${c.label.toLowerCase()} (${Math.round(((weights[c.id] || 0) / total) * 100)}%)`);
  const departs = preferred && preferred.id !== recommended.id;

  return (
    <div style={{ ...card, padding: 18, border: `1px solid ${ACCENT}` }}>
      <SectionTitle icon={FileText} right={<MockDataBadge>Mock — for TAB review</MockDataBadge>}>
        Recommendation summary
      </SectionTitle>
      <div style={{ fontSize: 13, color: "#c3d2e0", lineHeight: 1.65 }}>
        <p style={{ margin: "0 0 8px" }}>
          <strong style={{ color: TEXT }}>Need.</strong>{" "}
          {demandTitle ? `"${demandTitle}" — ` : ""}the team evaluated {ranked.length} alternatives,
          including the status quo, over a 5-year lifecycle using six weighted criteria, with{" "}
          {topCriteria.join(" and ")} weighted most heavily.
        </p>
        <p style={{ margin: "0 0 8px" }}>
          <strong style={{ color: TEXT }}>{departs ? "Preferred alternative." : "Recommended alternative."}</strong>{" "}
          <strong style={{ color: ACCENT }}>{chosen.name}</strong> (weighted score {chosen.weighted.toFixed(2)} of 5,
          rank {chosen.rank}). Estimated 5-year lifecycle cost is {fmtM(chosen.fiveYearCost)} ({fmtM(chosen.oneTime)} one-time,{" "}
          {fmtM(chosen.annualOM)}/yr O&amp;M)
          {chosen.id !== "opt1" && statusQuo
            ? `, against ${fmtM(statusQuo.fiveYearCost)} to maintain the current state — a net present value of ${fmtSignedM(chosen.npvVsStatusQuo)} versus the status quo`
            : ""}
          . {chosen.ioc ? `Initial operating capability in ~${chosen.ioc} months, full in ~${chosen.foc}.` : ""}{" "}
          Overall risk is <strong style={{ color: TEXT }}>{chosen.riskLevel.toLowerCase()}</strong>.
        </p>
        {departs && (
          <p style={{ margin: "0 0 8px", color: "#facb7e" }}>
            <strong>Note:</strong> the preferred alternative departs from the highest-scoring option,{" "}
            {recommended.name} ({recommended.weighted.toFixed(2)}). TAB should ask for the rationale.
          </p>
        )}
        <p style={{ margin: "0 0 8px" }}>
          <strong style={{ color: TEXT }}>Why.</strong> {chosen.missionFit}
        </p>
        <p style={{ margin: "0 0 8px" }}>
          <strong style={{ color: TEXT }}>Key risks to carry into the POC.</strong>{" "}
          {chosen.risks.slice(0, 2).join("; ")}.
        </p>
        {runnerUp && (
          <p style={{ margin: 0 }}>
            <strong style={{ color: TEXT }}>Next best.</strong> {runnerUp.name} ({runnerUp.weighted.toFixed(2)},{" "}
            {fmtM(runnerUp.fiveYearCost)}) remains the fallback if the POC does not validate the preferred approach.
          </p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ main */
/**
 * @param {string} demandId
 * @param {string} [demandTitle] - shown in the recommendation text
 * @param {(demandId: string) => void} [onAdvance] - override the default
 *   navigation to /poc/:demandId (e.g. Dashboard.js switching activeTab)
 */
export default function AoAWorkbench({ demandId, demandTitle, onAdvance }) {
  const navigate = useNavigate();
  const aoa = useMemo(() => buildMockAoa(demandId), [demandId]);
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [preferredId, setPreferredId] = useState(() => readPreferred(demandId));

  useEffect(() => {
    setPreferredId(readPreferred(demandId));
    setWeights(DEFAULT_WEIGHTS);
  }, [demandId]);

  const ranked = useMemo(() => rankAlternatives(aoa.alternatives, weights), [aoa, weights]);
  const recommended = ranked[0];
  const preferred = ranked.find((a) => a.id === preferredId) || null;
  const chosen = preferred || recommended;
  const statusQuo = ranked.find((a) => a.id === "opt1");

  const handlePrefer = (altId) => {
    setPreferredId(altId);
    writePreferred(demandId, altId);
  };

  const handleComplete = () => {
    // Follow-up: persist the AoA (weights, preferred alternative, summary) to the
    // backend and advance current_stage. For now this only navigates.
    if (!preferredId) writePreferred(demandId, recommended.id);
    if (onAdvance) onAdvance(demandId);
    else navigate(`/poc/${encodeURIComponent(demandId)}`);
  };

  return (
    <div style={{ color: TEXT, display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Intro + mock banner */}
      <div style={{ ...card, padding: 16, display: "flex", gap: 14, alignItems: "flex-start",
                    flexWrap: "wrap" }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: "#1a2a49",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Scale size={20} style={{ color: ACCENT }} />
        </div>
        <div style={{ flex: "1 1 320px" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 4 }}>
            What is the most effective, affordable, and lowest-risk way to meet this need?
          </div>
          <div style={{ fontSize: 12.5, color: MUTED, lineHeight: 1.55 }}>
            The Analysis of Alternatives compares candidate solutions — including doing nothing — on
            lifecycle cost, benefit, risk, schedule, technical feasibility and security posture. The
            preferred alternative moves into Enhanced Discovery (POC) for validation, and TAB reads
            the recommendation summary at decision time.
          </div>
        </div>
        <MockDataBadge>Mock data — illustrative figures</MockDataBadge>
      </div>

      {/* KPIs */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Kpi icon={Layers} label="Alternatives evaluated" value={ranked.length}
             sub="including status quo" />
        <Kpi icon={Trophy} label={preferred ? "Preferred option" : "Recommended option"}
             value={chosen.short} tone={ACCENT}
             sub={`score ${chosen.weighted.toFixed(2)} / 5 · rank ${chosen.rank}`} />
        <Kpi icon={DollarSign} label="Its 5-yr cost" value={fmtM(chosen.fiveYearCost)}
             sub={`${fmtM(chosen.oneTime)} one-time + ${fmtM(chosen.annualOM)}/yr`} />
        <Kpi icon={TrendingUp} label="NPV vs status quo"
             value={chosen.id === "opt1" ? "—" : fmtSignedM(chosen.npvVsStatusQuo)}
             tone={chosen.npvVsStatusQuo >= 0 ? "#34d17a" : "#ff6a33"}
             sub={statusQuo ? `status quo ${fmtM(statusQuo.fiveYearCost)} · ${Math.round(aoa.discountRate * 100)}% real rate` : ""} />
        <Kpi icon={CalendarClock} label="IOC / FOC"
             value={chosen.ioc ? `${chosen.ioc} / ${chosen.foc} mo` : "In place"}
             sub="months from award" />
      </div>

      {/* Weights + cost chart */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
        <WeightsPanel
          weights={weights}
          onChange={(id, v) => setWeights((w) => ({ ...w, [id]: v }))}
          onReset={() => setWeights(DEFAULT_WEIGHTS)}
        />
        <CostChart ranked={ranked} recommendedId={chosen.id} />
      </div>

      <ComparisonMatrix ranked={ranked} recommendedId={recommended.id} preferredId={preferredId} />

      {/* Detail cards */}
      <div>
        <SectionTitle icon={Layers}>Alternatives in detail</SectionTitle>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
          {ranked.map((a) => (
            <AlternativeCard
              key={a.id}
              alt={a}
              isRec={a.id === recommended.id}
              isPreferred={a.id === preferredId}
              onPrefer={handlePrefer}
            />
          ))}
        </div>
      </div>

      <Recommendation
        ranked={ranked}
        recommended={recommended}
        preferred={preferred}
        demandTitle={demandTitle}
        weights={weights}
      />

      {/* Advance */}
      <div style={{ ...card, padding: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <FlaskConical size={16} style={{ color: ACCENT }} />
        <div style={{ flex: "1 1 260px", fontSize: 12.5, color: MUTED }}>
          {preferred
            ? <>Preferred alternative: <strong style={{ color: TEXT }}>{preferred.name}</strong>. The POC will test this option.</>
            : <>No preferred alternative marked — completing will carry forward the highest-scoring option, <strong style={{ color: TEXT }}>{recommended.name}</strong>.</>}
          <div style={{ fontSize: 11, color: "#6b8aa8", marginTop: 2 }}>
            Saved in this browser only; nothing is written to the demand record yet.
          </div>
        </div>
        <button
          type="button"
          onClick={handleComplete}
          style={{ ...btn, background: ACCENT, borderColor: ACCENT, color: "#0b1a2d", padding: "8px 14px" }}
        >
          Complete AoA <ArrowRight size={13} /> advance to POC
        </button>
      </div>
    </div>
  );
}
